import { useEffect, useRef, useState } from "react";
import {
  ChallengeImage,
  ChallengeShell,
  useChoices,
  type ChallengeProps,
} from "./shared";
import { speak } from "../../audio";
import type { WordDTO } from "@shared/types";

/**
 * See a picture, listen to 4 audio bubbles, pick the one that matches.
 * Tap-once = play + select. Tap the SAME bubble again = commit. On a wrong
 * pick we hold a 3-second teaching moment showing the correct picture +
 * playing its audio so the student actually learns.
 */
export function ImageToAudio({ word, distractors, onAnswer }: ChallengeProps) {
  const choices = useChoices(word, distractors);
  const [selected, setSelected] = useState<WordDTO | null>(null);
  const [committed, setCommitted] = useState<string | null>(null);
  const [teaching, setTeaching] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, []);

  const later = (fn: () => void, ms: number) => {
    const timer = window.setTimeout(fn, ms);
    timers.current.push(timer);
  };

  const tap = (c: WordDTO) => {
    if (committed) return;
    if (selected?.id === c.id) {
      setCommitted(c.id);
      const correct = c.id === word.id;
      if (correct) {
        later(() => onAnswer(true), 800);
      } else {
        later(() => setTeaching(true), 600);
        later(() => speak(word.english, { audioUrl: word.audioUrl }), 900);
        later(() => onAnswer(false), 3600);
      }
      return;
    }
    setSelected(c);
    speak(c.english, { audioUrl: c.audioUrl });
  };

  return (
    <>
      <ChallengeShell
        tag="Picture sound"
        imageUrl={word.imageUrl}
        prompt={
          selected && !committed
            ? "Tap the same bubble again to choose it"
            : "Tap a bubble to hear it"
        }
      >
        <div className="audio-bubbles">
          {choices.map((c, i) => {
            const correct = c.id === word.id;
            const isSelected = selected?.id === c.id;
            const isCommitted = committed === c.id;
            let cls = "";
            if (committed) {
              cls = isCommitted
                ? correct
                  ? "good"
                  : "bad"
                : correct
                  ? "good faded"
                  : "faded";
            } else if (isSelected) {
              cls = "selected-pick";
            }
            return (
              <button
                key={c.id}
                className={`audio-bubble ${cls}`}
                onClick={() => tap(c)}
                disabled={!!committed}
                aria-label={`Sound ${i + 1}`}
              >
                <span className="audio-bubble-icon">🔊</span>
                <span className="audio-bubble-num">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </ChallengeShell>
            {teaching && (
              <div className="teach-overlay">
                <div className="teach-card">
                  <p className="muted teach-label">The answer was…</p>
                  {word.imageUrl && (
                    <ChallengeImage className="teach-image" src={word.imageUrl} alt={word.english} />
                  )}
                  <h2 className="teach-english">{word.english}</h2>
                  <div className="teach-progress" />
                </div>
        </div>
      )}
    </>
  );
}
